import React, { createContext, useContext, useState, useEffect } from "react";

const AuthContext = createContext();

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Helper function to get permissions based on role
const getPermissionsByRole = (role) => {
  const permissions = {
    admin: [
      "view_dashboard",
      "manage_users",
      "manage_assessments",
      "manage_learning_paths",
      "view_analytics",
      "manage_system_settings",
    ],
    instructor: [
      "view_dashboard",
      "manage_assessments",
      "manage_learning_paths",
      "view_analytics",
    ],
    student: [
      "view_dashboard",
      "take_assessments",
      "view_learning_paths",
      "view_progress",
      "enroll_courses",
    ],
  };
  return permissions[role] || permissions.student;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing session
    const token = localStorage.getItem("authToken");
    if (token) {
      // Verify token with backend
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const userData = {
          id: data.user.id,
          email: data.user.email,
          name: `${data.user.firstName} ${data.user.lastName}`,
          firstName: data.user.firstName,
          lastName: data.user.lastName,
          role: data.user.role,
          permissions: getPermissionsByRole(data.user.role),
        };
        setUser(userData);
      } else {
        // Token is invalid, remove it
        localStorage.removeItem("authToken");
        localStorage.removeItem("currentUser");
      }
    } catch (error) {
      console.error("Token verification failed:", error);
      localStorage.removeItem("authToken");
      localStorage.removeItem("currentUser");
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        const userData = {
          id: data.user.id,
          email: data.user.email,
          name: `${data.user.firstName} ${data.user.lastName}`,
          firstName: data.user.firstName,
          lastName: data.user.lastName,
          role: data.user.role,
          permissions: getPermissionsByRole(data.user.role),
        };

        setUser(userData);
        localStorage.setItem("authToken", data.token);
        localStorage.setItem("currentUser", JSON.stringify(userData));

        return { success: true, user: userData };
      } else {
        return {
          success: false,
          error: data.error || "Invalid credentials",
        };
      }
    } catch (error) {
      console.error("Login error:", error);
      return {
        success: false,
        error: "Network error. Please try again.",
      };
    }
  };

  const signup = async (userData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: userData.email,
          password: userData.password,
          firstName: userData.firstName,
          lastName: userData.lastName,
          termsAcceptedAt: userData.termsAcceptedAt,
          privacyAcceptedAt: userData.privacyAcceptedAt,
          acceptTerms: userData.acceptTerms,
          acceptPrivacy: userData.acceptPrivacy,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        const newUser = {
          id: data.user.id,
          email: data.user.email,
          name: `${data.user.firstName} ${data.user.lastName}`,
          firstName: data.user.firstName,
          lastName: data.user.lastName,
          role: data.user.role,
          permissions: getPermissionsByRole(data.user.role),
          termsAcceptedAt: data.user.termsAcceptedAt,
          privacyAcceptedAt: data.user.privacyAcceptedAt,
        };

        setUser(newUser);
        localStorage.setItem("authToken", data.token);
        localStorage.setItem("currentUser", JSON.stringify(newUser));

        // Store terms acceptance locally as well
        localStorage.setItem(
          "termsAcceptance",
          JSON.stringify({
            termsAcceptedAt: userData.termsAcceptedAt,
            privacyAcceptedAt: userData.privacyAcceptedAt,
            userAgent: navigator.userAgent,
            ipAddress: "client-side", // This would be captured server-side
          })
        );

        return { success: true, user: newUser };
      } else {
        return {
          success: false,
          error: data.error || "Registration failed",
        };
      }
    } catch (error) {
      console.error("Signup error:", error);
      return {
        success: false,
        error: "Network error. Please try again.",
      };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("authToken");
    localStorage.removeItem("currentUser");
  };

  const updateUser = (updatedUserData) => {
    const updatedUser = { ...user, ...updatedUserData };
    setUser(updatedUser);
    localStorage.setItem("currentUser", JSON.stringify(updatedUser));
  };

  const hasPermission = (permission) => {
    return user?.permissions?.includes(permission) || false;
  };

  const isAdmin = () => {
    return user?.role === "admin";
  };

  const isUser = () => {
    return user?.role === "student";
  };

  const isInstructor = () => {
    return user?.role === "instructor";
  };

  const value = {
    user,
    login,
    signup,
    logout,
    updateUser,
    hasPermission,
    isAdmin,
    isUser,
    isInstructor,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
